import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Errors, { getString } from "../../../components/errors/Errors";
import Listview from "../../../components/listview/Listview";
import { DefaultPagination, getPagination, Pagination } from "../../../components/listview/Pagination";
import useAbortController from "../../../hooks/useAbortController";
import useStatusBarState from "../../../hooks/useStatusBar";
import { Functionaries } from "../../../listsSettings/functionaries";
import { ErrorCode, IAction, IListviewField, SearchResult, UserPermissions } from "../../../services/types";
import { getCurrentUrlAsSource, getSelectedItems } from "../../../services/utils";
import { getUserPermissions } from "../../../services/webapi";
import strings from "../../../strings";
import Add from "../../../svg/Add.svg?react";
import Bin from "../../../svg/Bin.svg?react";
import * as Fields from "./Fields";
import { ChapterElectionFull } from "./types";
import { newForm } from "./urls";
import { deleteChapterElections, getChapterElectionFilters, getChapterElections } from "./webapi";

const AllItems: FC = () => {
  const [abortController, resetAbortController] = useAbortController();
  const listviewId = "chapter-elections-all-items";
  const fields: IListviewField<ChapterElectionFull>[] = [Fields.legislature, Fields.chapter, Fields.electionDate];

  const statusBar = useStatusBarState();
  const [chapterElections, setChapterElections] = useState<SearchResult<ChapterElectionFull> | null>(null);
  const [errorCode, setErrorCode] = useState<ErrorCode>();

  const [permissions, setPermissions] = useState<UserPermissions>();
  const navigateTo = useNavigate();

  const pagination = getPagination(listviewId) || {
    ...DefaultPagination,
    orderBy: Fields.legislature.fieldName,
  };

  useEffect(() => {
    loadChapterElections(pagination);

    return () => {
      abortController && abortController.abort();
    };
  }, []);

  const loadChapterElections = async (pagination: Pagination) => {
    const abortController = resetAbortController();
    const permissions = await getUserPermissions(
      Functionaries.Lists.ChapterElections.InternalName,
      Functionaries.InternalName,
      abortController.signal,
    );

    if (!abortController.signal.aborted) {
      setPermissions(permissions);
      const canRead = permissions.schemaPermission && permissions.listPermissions.includes("read-all");
      !canRead && setErrorCode(403);
      if (canRead) {
        const result = await getChapterElections(pagination, abortController.signal);
        if (!abortController.signal.aborted) {
          result.data && setChapterElections(result.data);
          result.error && setErrorCode(result.error.code);
        }
      }
    }
  };

  const newAction: IAction = {
    title: strings.new,
    icon: Add,

    onClick: () => navigateTo(`${newForm}?${getCurrentUrlAsSource()}`),
    disabled: !permissions || !permissions.listPermissions.includes("create"),
  };

  const deleteAction: IAction = {
    title: strings.delete,
    icon: Bin,
    onClick: async () => {
      const selectedItems = getSelectedItems(chapterElections);
      if (selectedItems.length > 0 && confirm(strings.confirmDeleteItems)) {
        const abortController = resetAbortController();
        const result = await deleteChapterElections(
          selectedItems.map((x) => x.id),
          abortController.signal,
        );

        if (result.error) {
          statusBar.addError(getString(result.error));
        } else if (result.data) {
          const s = selectedItems.filter((x) => x.id && result.data?.[x.id]);

          if (s.length != selectedItems.length) {
            statusBar.addWarning(strings.notAllElementsDeleted);
          }
          if (s.length > 0) {
            await loadChapterElections(pagination);
          }
        }
      }
    },
    disabled: getSelectedItems(chapterElections).length === 0 || !permissions?.listPermissions.includes("delete-all"),
  };

  const actions = [newAction, deleteAction];

  return (
    <>
      {permissions &&
        (!errorCode ? (
          <Listview
            id={listviewId}
            actions={actions}
            viewProperties={{
              schema: Functionaries.InternalName,
              list: Functionaries.Lists.ChapterElections.InternalName,
              name: "all_items",
              setListAndPage: true,
            }}
            fields={fields}
            allowSelectRows
            reload={loadChapterElections}
            searchResult={chapterElections}
            getFilters={getChapterElectionFilters}
            pagination={pagination}
            updateItems={(items) =>
              chapterElections &&
              setChapterElections({
                ...chapterElections,
                items: items,
              })
            }
          />
        ) : (
          errorCode && <Errors errorCode={errorCode} />
        ))}
    </>
  );
};

export default AllItems;
